"use client";

import { useState } from "react";
import { motion, type Variants } from "motion/react";
import {
    ArrowUpRight,
    ShieldCheck,
    ExternalLink,
} from "lucide-react";
import { socialProfiles, socialLinks } from "@/data/social";
import type { SocialProfile, SocialPlatform } from "@/types/social";

/* ── Motion ─────────────────────────────────────────────────────────────── */
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const gridVariants: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

/* ── Brand glyphs ───────────────────────────────────────────────────────── */
const InstagramGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.516 2.497 5.783 2.226 7.149 2.163 8.415 2.105 8.796 2.163 12 2.163Zm0-2.163C8.741 0 8.332.013 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.013 8.332 0 8.741 0 12c0 3.259.013 3.668.072 4.948.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.987 8.741 24 12 24s3.668-.013 4.948-.072c1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.059-1.28.072-1.689.072-4.948s-.013-3.668-.072-4.948c-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.013 15.259 0 12 0Zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881Z" />
    </svg>
);

const LinkedInGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M20.4 2H3.6A1.6 1.6 0 0 0 2 3.6v16.8A1.6 1.6 0 0 0 3.6 22h16.8a1.6 1.6 0 0 0 1.6-1.6V3.6A1.6 1.6 0 0 0 20.4 2ZM8 19H5V9.5h3V19ZM6.5 8.2a1.7 1.7 0 1 1 0-3.5 1.7 1.7 0 0 1 0 3.5ZM19 19h-3v-5c0-1.2-.4-2-1.5-2A1.6 1.6 0 0 0 13 13.6V19h-3V9.5h2.9v1.3a3.2 3.2 0 0 1 2.9-1.6c2.1 0 3.2 1.4 3.2 4V19Z" />
    </svg>
);

const FacebookGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z" />
    </svg>
);

const XGlyph = (p: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
        <path d="M18.9 2H22l-7.4 8.5L23 22h-6.8l-5.3-7-6.1 7H1.7l8-9.1L1 2h7l4.8 6.3L18.9 2Zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20Z" />
    </svg>
);

/* ── Platform config ────────────────────────────────────────────────────── */
const PLATFORM_CONFIG: Record<
    SocialPlatform,
    {
        label: string;
        accent: string;
        bg: string;
        Glyph: React.FC<React.SVGProps<SVGSVGElement>>;
        sandbox: string;
    }
> = {
    instagram: {
        label: "Instagram",
        accent: "#E1306C",
        bg: "from-[#833ab4]/10 via-[#fd1d1d]/10 to-[#fcb045]/10",
        Glyph: InstagramGlyph,
        sandbox: "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox",
    },
    linkedin: {
        label: "LinkedIn",
        accent: "#0A66C2",
        bg: "from-[#0A66C2]/10 to-[#0A66C2]/5",
        Glyph: LinkedInGlyph,
        sandbox: "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms",
    },
    facebook: {
        label: "Facebook",
        accent: "#1877F2",
        bg: "from-[#1877F2]/10 to-[#1877F2]/5",
        Glyph: FacebookGlyph,
        sandbox: "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms",
    },
    x: {
        label: "X",
        accent: "#17160F",
        bg: "from-[#17160F]/10 to-[#17160F]/5",
        Glyph: XGlyph,
        sandbox: "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox",
    },
};

/* ── Single profile card ────────────────────────────────────────────────── */
function ProfileCard({ profile }: { profile: SocialProfile }) {
    const [loaded, setLoaded] = useState(false);
    const cfg = PLATFORM_CONFIG[profile.platform];
    const { Glyph } = cfg;

    return (
        <motion.article
            variants={cardVariants}
            className="
        group relative flex flex-col overflow-hidden
        border border-[#17160F]/10 bg-[#FDFCFA]
        shadow-[0_1px_2px_rgba(23,22,15,0.04),0_18px_40px_-28px_rgba(23,22,15,0.22)]
        transition-shadow duration-500
        hover:shadow-[0_1px_2px_rgba(23,22,15,0.05),0_26px_56px_-28px_rgba(23,22,15,0.3)]
      "
        >
            {/* ── Top bar ── */}
            <div
                className={`flex items-center justify-between border-b border-[#17160F]/[0.07] bg-gradient-to-r ${cfg.bg} px-4 py-3 sm:px-5`}
            >
                <div className="flex items-center gap-2.5">
                    <span
                        className="grid h-8 w-8 place-items-center rounded-full bg-white/80 shadow-sm"
                        style={{ color: cfg.accent }}
                    >
                        <Glyph className="h-4 w-4" />
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#17160F]">
                        {cfg.label}
                    </span>
                </div>

                <a
                    href={profile.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${cfg.label} profile`}
                    className="
            grid h-7 w-7 place-items-center
            border border-[#17160F]/15 text-[#17160F]/50
            transition-colors duration-300
            hover:border-[#17160F] hover:text-[#17160F]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9C7A46]
          "
                >
                    <ExternalLink size={12} strokeWidth={1.8} />
                </a>
            </div>

            {/* ── Embed area ── */}
            <div className="relative min-h-[500px] w-full flex-1 bg-[#F0EDE7] sm:min-h-[560px]">
                {/* Skeleton shown while iframe loads */}
                {!loaded && (
                    <div className="absolute inset-0 z-[5] flex items-center justify-center bg-[#F0EDE7]">
                        <span
                            className="h-8 w-8 animate-spin rounded-full border-2 border-[#17160F]/15 border-t-[#9C7A46]"
                            aria-label="Loading"
                        />
                    </div>
                )}

                {/* Iframe — loads automatically on page load */}
                <iframe
                    src={profile.embedUrl}
                    title={`${cfg.label} profile feed`}
                    loading="lazy"
                    sandbox={cfg.sandbox}
                    referrerPolicy="strict-origin-when-cross-origin"
                    onLoad={() => setLoaded(true)}
                    className={`
            absolute inset-0 h-full w-full border-0 bg-white
            transition-opacity duration-500
            ${loaded ? "opacity-100" : "opacity-0"}
          `}
                />
            </div>

            {/* ── Caption plate ── */}
            <div className="flex items-center justify-between gap-4 border-t border-[#17160F]/[0.07] px-4 py-3.5 sm:px-5">
                {profile.caption && (
                    <p className="text-[12px] font-light leading-relaxed text-[#5A5548]">
                        {profile.caption}
                    </p>
                )}
                <a
                    href={profile.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
            ml-auto shrink-0 inline-flex items-center gap-1.5
            text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9C7A46]
            transition-colors duration-300 hover:text-[#17160F]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9C7A46]
          "
                >
                    Follow
                    <ArrowUpRight size={11} strokeWidth={2.2} />
                </a>
            </div>
        </motion.article>
    );
}

/* ── Section ────────────────────────────────────────────────────────────── */
export function SocialEmbedSection({
    profiles = socialProfiles,
}: {
    profiles?: SocialProfile[];
}) {
    if (!profiles.length) return null;

    return (
        <section aria-labelledby="social-heading" className="relative w-full overflow-x-clip bg-[#F7F5F0] py-16 sm:py-20 lg:py-28">
            {/* Container width increased to max-w-[1600px] */}
            <div className="mx-auto max-w-[1600px] px-[max(1.25rem,env(safe-area-inset-left))] sm:px-8 lg:px-12 xl:px-16">
                {/* ── Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16"
                >
                    {/* Title block */}
                    <div className="relative min-w-0 pl-5 sm:pl-8">
                        <span
                            aria-hidden="true"
                            className="absolute bottom-1 left-0 top-1 w-px bg-gradient-to-b from-[#9C7A46] via-[#9C7A46]/30 to-transparent"
                        />
                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.28em] text-[#9C7A46] sm:text-[12px] sm:tracking-[0.4em]">
                            Broadcast
                            <span className="mx-2 text-[#17160F]/25">—</span>
                            Social
                        </p>
                        <h2
                            id="social-heading"
                            className="
                mt-4 flex flex-col font-display leading-[0.94] tracking-tight text-[#17160F]
                text-[clamp(2rem,8.5vw,3.2rem)]
                sm:mt-5 sm:text-[clamp(2.6rem,6vw,4rem)]
                lg:mt-6 lg:leading-[0.92] lg:text-[clamp(3rem,4.4vw,4.6rem)]
                [overflow-wrap:anywhere]
              "
                        >
                            <span className="font-light">Follow the</span>
                            <span className="font-black uppercase tracking-tighter">Making</span>
                        </h2>
                        <p className="mt-5 max-w-[46ch] text-[14.5px] font-light leading-[1.7] text-[#5A5548] sm:text-[15px] lg:text-[16px]">
                            Process notes, films and dispatches — gathered across every channel.
                        </p>
                    </div>

                    {/* Follow icon rail */}
                    <div className="flex shrink-0 flex-wrap items-center gap-2.5 lg:mb-1">
                        {(Object.keys(socialLinks) as (keyof typeof socialLinks)[]).map((key) => {
                            const cfg = PLATFORM_CONFIG[key];
                            const G = cfg.Glyph;
                            return (
                                <a
                                    key={key}
                                    href={socialLinks[key]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Follow us on ${cfg.label}`}
                                    className="
                    grid h-12 w-12 place-items-center
                    border border-[#17160F]/15 bg-transparent text-[#17160F]
                    transition-colors duration-300
                    hover:border-[#17160F] hover:bg-[#17160F] hover:text-[#F7F5F0]
                    focus-visible:outline-none focus-visible:ring-2
                    focus-visible:ring-[#9C7A46] focus-visible:ring-offset-2
                    focus-visible:ring-offset-[#F7F5F0]
                  "
                                >
                                    <G className="h-[17px] w-[17px]" />
                                </a>
                            );
                        })}
                    </div>
                </motion.div>

                <div className="mt-10 h-px w-full bg-[#17160F]/10 sm:mt-12" />
            </div>

            {/* ── Four-column grid (Expanded max-width) ── */}
            <motion.div
                variants={gridVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="
          mx-auto mt-10 grid max-w-[1600px] gap-6
          px-[max(1.25rem,env(safe-area-inset-left))]
          sm:px-8 sm:mt-12
          sm:grid-cols-2
          lg:grid-cols-4 lg:px-12 lg:mt-14
          xl:px-16
        "
            >
                {profiles.map((profile) => (
                    <ProfileCard key={profile.id} profile={profile} />
                ))}
            </motion.div>

            {/* ── Privacy note (Expanded max-width) ── */}
            <div
                className="
          mx-auto mt-8 flex max-w-[1600px] items-start gap-2.5
          px-[max(1.25rem,env(safe-area-inset-left))]
          sm:mt-10 sm:px-8 lg:px-12 xl:px-16
        "
            >
                <ShieldCheck
                    size={13}
                    strokeWidth={1.8}
                    className="mt-px shrink-0 text-[#9C7A46]"
                />
                <p className="text-[11px] font-light leading-relaxed text-[#5A5548]/80">
                    Embedded social feeds load automatically on page render. Embedded third-party services may set tracking cookies under their respective privacy policies.
                </p>
            </div>
        </section>
    );
}

export default SocialEmbedSection;